/* The focus model (Ale ruling 2026-09-02).
 *
 * One object on the fund page is "in focus": the officer clicked it on
 * the diagram, on a record row, or arrived with `?focus=` in the URL. The
 * focus names the object; the VERTICAL SECTION is the object plus its
 * ancestors — straight up to the fund, nothing beside, nothing below.
 *
 *   fund → SPV → targets A, B, C   (and a second SPV elsewhere)
 *   click B (or B's instrument, or the holding in B) → fund → SPV → B
 *   click the SPV                                    → fund → SPV
 *   click the fund                                   → the fund alone
 *
 * The section is computed from the CURRENT record every render — never a
 * stored copy (officer-UI principle: what stands on the record now).
 *
 * This module is pure (its only import is the equally pure `chainTree`)
 * so it can be unit-tested with the bare node runner (there is no vitest
 * in this repo). The segment shape is the structural subset of
 * `RtSegment` the section needs — the same shape the tree lays out.
 */

import { chainOf, fundRootId, type ChainSegment } from './chainTree.js'

export type Focus =
  | { kind: 'fund' }
  | { kind: 'entity'; id: string }
  | { kind: 'instrument'; id: string }
  | { kind: 'segment'; id: string }

export type FocusSegment = ChainSegment

/** A record object as the drawer addresses it. */
export interface FocusTarget {
  type: 'entity' | 'instrument' | 'position' | 'trade'
  id: string
}

/* ── URL form: `fund` | `entity:<id>` | `instrument:<id>` | `segment:<id>` ── */

export function parseFocus(raw: string | null | undefined): Focus | null {
  if (!raw) return null
  if (raw === 'fund') return { kind: 'fund' }
  const m = /^(entity|instrument|segment):(.+)$/.exec(raw)
  if (!m) return null
  return { kind: m[1] as 'entity' | 'instrument' | 'segment', id: m[2] }
}

export function serializeFocus(f: Focus): string {
  return f.kind === 'fund' ? 'fund' : `${f.kind}:${f.id}`
}

export function sameFocus(a: Focus | null, b: Focus | null): boolean {
  if (!a || !b) return a === b
  return serializeFocus(a) === serializeFocus(b)
}

/* ── The chain as the section sees it ──────────────────────────────────── */

/** The chain helpers live in `chainTree` (one implementation — the same
 *  tree the Overview draws); re-exported here so every focus-model caller
 *  keeps one import. */
export { chainOf, fundRootId }

/** The leg the focus anchors on — the one that carries the focused object
 *  on its lower end (issuer / instrument / position). The fund itself
 *  anchors nowhere: it is the top, not a leg. */
export function anchorSegment<S extends FocusSegment>(chain: S[], focus: Focus): S | null {
  switch (focus.kind) {
    case 'fund':
      return null
    case 'entity':
      return chain.find((s) => s.issuer_id === focus.id) ?? null
    case 'instrument':
      return chain.find((s) => s.instrument_id === focus.id) ?? null
    case 'segment':
      return (
        chain.find((s) => s.position_id === focus.id || s.position_ids.includes(focus.id)) ?? null
      )
  }
}

/** The parent leg of `s`: the one whose issuer is s's holder, one level up.
 *  Where an entity is reached by more than one leg, the earliest in chain
 *  order wins (a linear section, never a fan). */
function parentOf<S extends FocusSegment>(chain: S[], s: S): S | null {
  const above = chain.filter((p) => p.issuer_id === s.holder_id && p.depth < s.depth)
  if (above.length === 0) return null
  return above.reduce((a, b) => (b.depth > a.depth ? b : a))
}

/** Every leg that carries the focused object on its lower end. An entity
 *  reached by two instruments from the same holder (Nerine holds both
 *  Namenaktien and Partizipationsscheine in Silvretta) is reached by
 *  BOTH legs — both are straight above it. A CO-OWNED instrument (one
 *  paper, two holders — an OpCo held by two SPVs) is likewise reached by
 *  every holder's leg, so focusing it climbs through ALL its holders
 *  (Ale 15a, 2026-09-03). A single holding is one leg. */
export function anchorSegments<S extends FocusSegment>(chain: S[], focus: Focus): S[] {
  if (focus.kind === 'entity') return chain.filter((s) => s.issuer_id === focus.id)
  if (focus.kind === 'instrument') return chain.filter((s) => s.instrument_id === focus.id)
  const one = anchorSegment(chain, focus)
  return one ? [one] : []
}

/** THE RULING. Ordered from the focused object UP to the fund: the
 *  anchor leg(s) first, then their parents, … , the fund's own leg last.
 *  Empty for the fund itself, and for anything not on the chain. */
export function verticalSection<S extends FocusSegment>(chain: S[], focus: Focus): S[] {
  const anchors = anchorSegments(chain, focus)
  if (anchors.length === 0) return []
  const out: S[] = []
  const seen = new Set<string>()
  // Breadth-first up the chain: all anchors, then all their parents, …
  let level: S[] = anchors
  while (level.length > 0) {
    const next: S[] = []
    for (const cur of level) {
      if (seen.has(cur.position_id)) continue
      seen.add(cur.position_id)
      out.push(cur)
      const p = parentOf(chain, cur)
      if (p) next.push(p)
    }
    level = next
  }
  return out
}

/** Every object id on the section (holders, instruments, positions,
 *  issuers) plus the fund root — what the diagram lights and the record
 *  keeps. With no focus, nothing is computed (null = show everything). */
export function sectionObjectIds(
  chain: FocusSegment[],
  focus: Focus | null,
  knownHeadId?: string | null
): Set<string> | null {
  if (!focus) return null
  const ids = new Set<string>()
  const root = fundRootId(chain, knownHeadId)
  if (root) ids.add(root)
  for (const s of verticalSection(chain, focus)) {
    ids.add(s.holder_id)
    ids.add(s.position_id)
    for (const pid of s.position_ids) ids.add(pid)
    if (s.instrument_id) ids.add(s.instrument_id)
    if (s.issuer_id) ids.add(s.issuer_id)
  }
  return ids
}

/** Is this leg on the section? (position identity — the leg, not its ends,
 *  so a sibling leg of the same holder stays out.) */
export function segmentOnSection(section: FocusSegment[], s: FocusSegment): boolean {
  return section.some((x) => x.position_id === s.position_id)
}

/* ── From a clicked object to a focus, and back to a drawer target ─────── */

/** The focus a click on `target` sets. The fund's own entity focuses the
 *  fund; a trade focuses the leg it moved. */
export function focusForTarget(
  chain: FocusSegment[],
  target: FocusTarget,
  tradePositionId?: string | null,
  knownHeadId?: string | null
): Focus | null {
  switch (target.type) {
    case 'entity':
      return fundRootId(chain, knownHeadId) === target.id
        ? { kind: 'fund' }
        : { kind: 'entity', id: target.id }
    case 'instrument':
      return { kind: 'instrument', id: target.id }
    case 'position':
      return { kind: 'segment', id: target.id }
    case 'trade': {
      if (!tradePositionId) return null
      const leg = chain.find(
        (s) => s.position_id === tradePositionId || s.position_ids.includes(tradePositionId)
      )
      return leg ? { kind: 'segment', id: leg.position_id } : null
    }
  }
}

/** The drawer target a URL focus opens. */
export function targetForFocus(
  chain: FocusSegment[],
  focus: Focus,
  knownHeadId?: string | null
): FocusTarget | null {
  switch (focus.kind) {
    case 'fund': {
      const root = fundRootId(chain, knownHeadId)
      return root ? { type: 'entity', id: root } : null
    }
    case 'entity':
      return { type: 'entity', id: focus.id }
    case 'instrument':
      return { type: 'instrument', id: focus.id }
    case 'segment':
      return { type: 'position', id: focus.id }
  }
}

/* ── Officer sentences ─────────────────────────────────────────────────── */

/** The focused object by name, as the record names it. */
export function focusName(
  chain: FocusSegment[],
  focus: Focus,
  fundName: string,
  entityName?: (id: string) => string | null
): string {
  const anchor = anchorSegment(chain, focus)
  switch (focus.kind) {
    case 'fund':
      return fundName
    case 'entity':
      return anchor?.issuer_name ?? entityName?.(focus.id) ?? 'this entity'
    case 'instrument':
      return anchor
        ? `${anchor.instrument_name ?? 'the instrument'} issued by ${anchor.issuer_name ?? 'its issuer'}`
        : 'this instrument'
    case 'segment':
      return anchor
        ? `the position in ${anchor.instrument_name ?? 'the instrument'} issued by ${anchor.issuer_name ?? 'its issuer'}`
        : 'this position'
  }
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** One sentence naming the section, top to bottom in officer words:
 *  "Borealis Growth Fund III, held through Aurora Holdings S.à r.l. by
 *  Aurora Global FoF". Direct holdings say so; the fund says it is the
 *  fund; an object off the chain says that too. */
export function sectionSentence(
  chain: FocusSegment[],
  focus: Focus,
  fundName: string,
  entityName?: (id: string) => string | null
): string {
  const name = focusName(chain, focus, fundName, entityName)
  if (focus.kind === 'fund') return `${fundName} — the fund itself, nothing above it`
  const section = verticalSection(chain, focus)
  if (section.length === 0) return `${name} — not on the fund's chain`
  // The top is the fund's own leg; every other holder on the section is
  // an intermediate — named once, nearest first (two legs from the same
  // holder, e.g. two instruments in one target, name that holder once).
  const rootId = fundRootId(chain)
  const topName = section.find((s) => s.holder_id === rootId)?.holder_name ?? fundName
  const holders: string[] = []
  for (const s of section) {
    if (s.holder_id === rootId) continue
    const h = s.holder_name ?? 'an intermediate holder'
    if (!holders.includes(h)) holders.push(h)
  }
  if (holders.length === 0) return `${name}, held directly by ${topName}`
  return `${name}, held through ${joinNames(holders)} by ${topName}`
}

/** The chip: "showing the section above <name>". */
export function focusChipLabel(chain: FocusSegment[], focus: Focus, fundName: string): string {
  if (focus.kind === 'fund') return 'showing the fund alone'
  return `showing the section above ${focusName(chain, focus, fundName)}`
}
